<div class="packages-table-container">
  <div class="table-header">
    <p-iconfield iconPosition="left">
      <p-inputicon>
        <i class="pi pi-search"></i>
      </p-inputicon>
      <input type="text" pInputText placeholder="Search packages..." (input)="onSearch($event)" />
    </p-iconfield>
    <p-button label="Create Package" icon="pi pi-plus" severity="primary" (onClick)="onCreatePackage()"></p-button>
  </div>

  <p-table
    [scrollable]="true"
    [value]="filteredPackages()"
    [loading]="isLoading()"
    [paginator]="true"
    [rows]="10"
    [rowsPerPageOptions]="[10, 25, 50]"
    [tableStyle]="{ 'min-width': '50rem' }"
  >
    <ng-template pTemplate="header">
      <tr>
        @for (col of columns(); track col.field) {
          <th [pSortableColumn]="col.sortable ? col.field : undefined">
            {{ col.header }}
            @if (col.sortable) {
              <p-sortIcon></p-sortIcon>
            }
          </th>
        }
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-pkg>
      <tr>
        <td>
          <strong>{{ pkg.name }}</strong>
        </td>
        <td>
          <code class="code-cell">{{ pkg.packageCode || '—' }}</code>
        </td>
        <td>
          <span class="secondary-text">{{ pkg.description || 'No description' }}</span>
        </td>
        <td>
          <strong>{{ formatCurrency(pkg.baselinePrice) }}</strong>
        </td>
        <td>
          <div class="options-count">
            @if (
              (pkg.optionCount !== undefined && pkg.optionCount > 0) || (pkg.optionIds && pkg.optionIds.length > 0)
            ) {
              @let count = pkg.optionCount ?? pkg.optionIds?.length ?? 0;
              <p-tag severity="info" [value]="count + ' option' + (count !== 1 ? 's' : '')"></p-tag>
            } @else {
              <span class="secondary-text">No options</span>
            }
          </div>
        </td>
        <td>
          <p-tag [value]="getStatusLabel(pkg.isActive)" [severity]="getStatusSeverity(pkg.isActive)"></p-tag>
        </td>
        <td>{{ formatDate(pkg.createdOn) }}</td>
        <td>
          <div class="action-buttons">
            <p-button
              icon="pi pi-pencil"
              severity="info"
              pTooltip="Edit"
              tooltipPosition="top"
              [text]="true"
              [rounded]="true"
              (onClick)="onEditPackage(pkg)"
            ></p-button>
            <p-button
              icon="pi pi-trash"
              severity="danger"
              pTooltip="Delete"
              tooltipPosition="top"
              [text]="true"
              [rounded]="true"
              [loading]="deletingId() === pkg.packageId"
              (onClick)="onDeletePackage(pkg)"
            ></p-button>
          </div>
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td [attr.colspan]="columns().length">
          <div class="empty-state">
            <i class="pi pi-box large-icon"></i>
            <p>No packages found</p>
            @if (searchTerm()) {
              <small>Try adjusting your search criteria</small>
            } @else {
              <small>Create your first package to get started</small>
            }
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>

<p-dialog
  [header]="dialogTitle()"
  [modal]="true"
  [style]="{ width: '50vw' }"
  [draggable]="false"
  [resizable]="false"
  [(visible)]="showDialog"
>
  <div class="dialog-content">
    @if (isEditMode() && selectedPackageCode()) {
      <div class="form-field">
        <label for="packageCode">Package Code</label>
        <input
          class="field-input"
          id="packageCode"
          type="text"
          pInputText
          readonly
          [value]="selectedPackageCode()"
        />
        <small class="help-text">Auto-generated identifier (read-only)</small>
      </div>
    }

    <div class="form-field">
      <label for="name">Package Name *</label>
      <input
        class="field-input"
        id="name"
        type="text"
        pInputText
        placeholder="Enter package name"
        [formField]="packageForm.name"
      />
      @if (packageForm.name().touched() && !packageForm.name().valid()) {
        <small class="p-error">
          @for (error of packageForm.name().errors(); track error.kind) {
            {{ error.message }}
          }
        </small>
      }
    </div>

    <div class="form-field">
      <label for="description">Description</label>
      <textarea
        class="field-input"
        id="description"
        placeholder="Enter package description"
        rows="3"
        [formField]="packageForm.description"
      ></textarea>
    </div>

    <div class="form-field">
      <label for="categoryCode">Category Code *</label>
      <input
        class="field-input"
        id="categoryCode"
        type="text"
        pInputText
        placeholder="e.g. MKT"
        [formField]="packageForm.categoryCode"
      />
      @if (packageForm.categoryCode().touched() && !packageForm.categoryCode().valid()) {
        <small class="p-error">
          @for (error of packageForm.categoryCode().errors(); track error.kind) {
            {{ error.message }}
          }
        </small>
      }
    </div>

    <div class="form-field">
      <label for="shorthandCode">Shorthand Code *</label>
      <input
        class="field-input"
        id="shorthandCode"
        type="text"
        pInputText
        placeholder="e.g. STARTER"
        [formField]="packageForm.shorthandCode"
      />
      @if (packageForm.shorthandCode().touched() && !packageForm.shorthandCode().valid()) {
        <small class="p-error">
          @for (error of packageForm.shorthandCode().errors(); track error.kind) {
            {{ error.message }}
          }
        </small>
      }
    </div>

    <div class="form-field">
      <label for="baselinePrice">Base Price *</label>
      <p-inputNumber
        class="field-input"
        id="baselinePrice"
        name="baselinePrice"
        mode="currency"
        currency="USD"
        locale="en-US"
        [min]="0"
        [ngModel]="packageForm.baselinePrice().value()"
        (ngModelChange)="packageForm.baselinePrice().value.set($event)"
      ></p-inputNumber>
    </div>

    <div class="form-field">
      <label for="optionIds">Options</label>
      <p-multiselect
        class="field-input"
        id="optionIds"
        optionLabel="name"
        optionValue="optionId"
        placeholder="Select options"
        appendTo="body"
        [ngModel]="packageFormModel().optionIds"
        [options]="options()"
        [showToggleAll]="true"
        [filter]="true"
        (ngModelChange)="onOptionsChange($event)"
      ></p-multiselect>
    </div>

    <div class="form-field">
      <label for="productLine">Product Line</label>
      <p-select
        class="field-input"
        id="productLine"
        optionLabel="label"
        optionValue="value"
        [ngModel]="packageFormModel().productLine"
        [options]="productLineOptions()"
        (ngModelChange)="onProductLineChange($event)"
      ></p-select>
    </div>

    <div class="form-field">
      <label for="pricingFrequency">Pricing Frequency</label>
      <p-select
        class="field-input"
        id="pricingFrequency"
        optionLabel="label"
        optionValue="value"
        [ngModel]="packageFormModel().pricingFrequency"
        [options]="pricingFrequencyOptions()"
        (ngModelChange)="onPricingFrequencyChange($event)"
      ></p-select>
    </div>

    <div class="form-field">
      <label for="oneTimeFee">One-Time Fee</label>
      <p-inputNumber
        class="field-input"
        id="oneTimeFee"
        name="oneTimeFee"
        mode="currency"
        currency="USD"
        locale="en-US"
        [ngModel]="packageForm.oneTimeFee().value()"
        (ngModelChange)="packageForm.oneTimeFee().value.set($event)"
      ></p-inputNumber>
      <small class="help-text">Optional one-time charge in addition to the base price</small>
    </div>

    <div class="form-field checkbox-field">
      <label>
        <input type="checkbox" [formField]="packageForm.isActive" />
        <span>Active (package is shown to users)</span>
      </label>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <p-button label="Cancel" severity="secondary" [disabled]="saving()" (onClick)="showDialog.set(false)"></p-button>
    <p-button
      [label]="dialogButtonLabel()"
      [loading]="saving()"
      [disabled]="!isFormValid()"
      (onClick)="onSavePackage()"
    ></p-button>
  </ng-template>
</p-dialog>
